import AttachFileIcon from '@mui/icons-material/AttachFile';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { FileUploadMatchColumns } from 'features/co-pilot/co-pilot.types';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiLink from 'features/ui/base-components/pai-link';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import LeadListColumnSelection from './lead-list-column-selection';

interface Props {
  open: boolean;
  columns: { text: string; type: string }[];
  data: string[][];
  fileName: string;
  hasTableHeading: boolean;
  handleColumnTypeChange(idx: number): (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleHasTableHeadingChange(event: React.ChangeEvent<HTMLInputElement>): void;
  handleClose(): void;
  handleAcceptColumns(): void;
}

const LeadListUploadDialog: React.FC<Props> = ({
  open,
  columns,
  fileName,
  hasTableHeading,
  handleColumnTypeChange,
  handleHasTableHeadingChange,
  handleClose,
  handleAcceptColumns,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  // Require either (First & Last Name) OR (Position / Role) together with Company
  const hasFirstName = _.some(columns, (col) => col.type === FileUploadMatchColumns.FIRST_NAME);
  const hasLastName = _.some(columns, (col) => col.type === FileUploadMatchColumns.LAST_NAME);
  const hasCompany = _.some(columns, (col) => col.type === FileUploadMatchColumns.COMPANY);
  const hasRoleOrPosition = _.some(columns, (col) =>
    [FileUploadMatchColumns.ROLE, FileUploadMatchColumns.POSITION].includes(col.type as FileUploadMatchColumns)
  );

  const isSelectionValid = hasCompany && ((hasFirstName && hasLastName) || hasRoleOrPosition);

  return (
    <Dialog classes={{ paper: classes.paper }} open={open} fullWidth maxWidth="md" transitionDuration={0}>
      <DialogTitle className={classes.title}>
        <Box display="flex" alignItems="center" gap={1}>
          <AttachFileIcon sx={{ color: '#FFF' }} />
          <Typography className={classes.titleText}>{fileName}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Stack gap={2}>
          <LeadListColumnSelection
            columns={columns}
            fileName={fileName}
            hasTableHeading={hasTableHeading}
            handleColumnTypeChange={handleColumnTypeChange}
            handleHasTableHeadingChange={handleHasTableHeadingChange}
            handleResetFileColumnSelection={handleClose} // reuse close as reset
          />
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiLink text={t('cancel')} size={'sm'} underline="always" action={handleClose} />
        <PaiButton text={t('confirmSelection')} border={'rounded'} size={'small'} action={handleAcceptColumns} disabled={!isSelectionValid} />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '20px 24px !important',
    whiteSpace: 'pre-wrap',
  },
  sectionTitle: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
  },
  label: {
    fontSize: theme.typography.pxToRem(14),
  },
  caption: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.text.secondary,
  },
  description: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },
  alert: {
    margin: '0 !important',
    borderRadius: '8px',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default LeadListUploadDialog;
